import { Vendor, ListingStatus, ApiResult } from '../../../types';
import { VendorMapper } from '../mappers/vendor.mapper';
import { VENDORS_KEY } from './constants';

export class LocalVendorsRepo {
    async getVendorById(id: string): Promise<Vendor | undefined> {
        const all = this.loadVendors();
        const found = all.find(v => v.id === id);
        return found ? VendorMapper.toDomain(found) : undefined;
    }

    async getVendors(filters?: any): Promise<Vendor[]> {
        let rows = this.loadVendors().map(r => VendorMapper.toDomain(r));

        if (filters?.category && filters.category !== 'all') {
            rows = rows.filter(v => v.category === filters.category);
        }

        if (filters?.query) {
            const query = String(filters.query).toLowerCase();
            rows = rows.filter(v =>
                v.title.toLowerCase().includes(query) ||
                v.location.toLowerCase().includes(query) ||
                v.description.toLowerCase().includes(query)
            );
        }

        if (!filters?.admin) {
            rows = rows.filter(v => v.status === ListingStatus.APPROVED || !v.status);
        }

        return rows;
    }

    async getMyVendors(userId: string): Promise<Vendor[]> {
        return this.loadVendors()
            .map(r => VendorMapper.toDomain(r))
            .filter(v => v.seller.id === userId);
    }

    async addVendor(vendor: Vendor, userId: string): Promise<ApiResult<Vendor>> {
        const all = this.loadVendors();
        all.push(VendorMapper.toRow(vendor, userId));
        this.saveVendors(all);
        return { success: true, data: vendor };
    }

    async updateVendor(vendor: Vendor, userId: string): Promise<ApiResult<Vendor>> {
        const all = this.loadVendors();
        const idx = all.findIndex(v => v.id === vendor.id);
        if (idx === -1) return { success: false, error: 'Not found' };

        all[idx] = { ...all[idx], ...VendorMapper.toRow(vendor, userId) };
        this.saveVendors(all);

        return { success: true, data: VendorMapper.toDomain(all[idx]) };
    }

    async deleteVendor(id: string): Promise<ApiResult<void>> {
        let all = this.loadVendors();
        const initialLen = all.length;
        all = all.filter(v => v.id !== id);

        if (all.length === initialLen) {
            return { success: false, error: 'Not found' };
        }

        this.saveVendors(all);
        return { success: true, data: undefined };
    }

    private loadVendors(): any[] {
        try {
            const stored = localStorage.getItem(VENDORS_KEY);
            if (stored) return JSON.parse(stored);
        } catch (e) {
            console.error('Failed to parse localStorage vendors', e);
        }
        return [];
    }

    private saveVendors(rows: any[]) {
        try {
            localStorage.setItem(VENDORS_KEY, JSON.stringify(rows));
        } catch (e) {
            console.error('Failed to save vendors', e);
        }
    }
}
